import { useMemo, useEffect, useState } from 'react';
import * as d3 from 'd3';
import { useQuery } from '@tanstack/react-query';
import { deriveCity } from '@genealogy-app/shared';
import { getPersonOrigins } from '@/api/persons.api';
import { QUERY_KEYS } from '@/api/queryKeys';
import { geocodePlaces, type GeoResult } from '@/utils/geocode';

// ─── Farben ──────────────────────────────────────────────────────────────────

const COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#f97316', '#ec4899', '#14b8a6', '#84cc16',
  '#6366f1', '#a855f7',
];
const UNKNOWN_COLOR = '#94a3b8';

// ─── Hilfsfunktionen ─────────────────────────────────────────────────────────

interface Slice {
  label: string;
  percentage: number;
  startAngle: number;
  endAngle: number;
  color: string;
}

function buildSlices(entries: Array<{ label: string; percentage: number }>, colorOffset = 0): Slice[] {
  if (!entries.length) return [];

  const pie = d3.pie<{ label: string; percentage: number }>()
    .value((d) => d.percentage)
    .sort(null);

  return pie(entries).map((arc, i) => ({
    label: arc.data.label,
    percentage: arc.data.percentage,
    startAngle: arc.startAngle,
    endAngle: arc.endAngle,
    color: arc.data.label === 'Unbekannt' ? UNKNOWN_COLOR : COLORS[(i + colorOffset) % COLORS.length],
  }));
}

// ─── Einzelnes Donut-Diagramm ─────────────────────────────────────────────────

const SIZE = 110;
const RADIUS = SIZE / 2;
const INNER_RADIUS = RADIUS * 0.42;

const arcGen = d3.arc<{ startAngle: number; endAngle: number }>()
  .innerRadius(INNER_RADIUS)
  .outerRadius(RADIUS)
  .padAngle(0.03)
  .cornerRadius(2);

function DonutChart({ slices, title }: { slices: Slice[]; title: string }) {
  return (
    <div>
      <p className="mb-2 text-xs font-medium text-slate-400">{title}</p>
      <div className="flex items-start gap-3">
        <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} className="shrink-0">
          <g transform={`translate(${RADIUS}, ${RADIUS})`}>
            {slices.map((slice) => {
              const d = arcGen({ startAngle: slice.startAngle, endAngle: slice.endAngle });
              return (
                <path key={slice.label} d={d ?? undefined} fill={slice.color} className="transition-opacity hover:opacity-75">
                  <title>{slice.label}: {slice.percentage}%</title>
                </path>
              );
            })}
          </g>
        </svg>

        <ul className="min-w-0 flex-1 space-y-1">
          {slices.map((slice) => (
            <li key={slice.label} className="flex items-center gap-1.5 text-xs">
              <span className="h-2 w-2 shrink-0 rounded-sm" style={{ backgroundColor: slice.color }} />
              <span className="min-w-0 truncate text-slate-600" title={slice.label}>{slice.label}</span>
              <span className="ml-auto shrink-0 font-medium tabular-nums text-slate-700">{slice.percentage}%</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ─── OriginsChart ─────────────────────────────────────────────────────────────

interface OriginsChartProps {
  personId: string;
}

/**
 * Aggregiert die (nach vollem Ortstext gruppierten) Origins über einen
 * abgeleiteten Schlüssel (Stadt bzw. Land) neu und berechnet die Prozentsätze.
 */
function aggregateBy(
  origins: Array<{ label: string; weight: number }>,
  keyOf: (label: string) => string,
): Array<{ label: string; percentage: number }> {
  const weights = new Map<string, number>();
  for (const o of origins) {
    const key = o.label === 'Unbekannt' ? 'Unbekannt' : keyOf(o.label);
    weights.set(key, (weights.get(key) ?? 0) + o.weight);
  }
  const total = Array.from(weights.values()).reduce((a, b) => a + b, 0);
  return Array.from(weights.entries())
    .map(([label, weight]) => ({
      label,
      weight,
      percentage: total > 0 ? Math.round((weight / total) * 1000) / 10 : 0,
    }))
    .sort((a, b) => b.weight - a.weight);
}

/** Land aus einem Geocoding-Treffer (Nominatim display_name endet mit dem Land). */
function countryFromGeo(geo: GeoResult | undefined): string | null {
  if (!geo) return null;
  return geo.displayName.split(',').map((s) => s.trim()).filter(Boolean).pop() ?? null;
}

export function OriginsChart({ personId }: OriginsChartProps) {
  const { data, isLoading } = useQuery({
    queryKey: QUERY_KEYS.origins.byPerson(personId),
    queryFn: () => getPersonOrigins(personId),
    staleTime: 5 * 60 * 1000,
  });

  // Stadt: rein lokal aus dem Ortstext (deriveCity) – schneidet das Landsegment ab.
  const citySlices = useMemo(
    () => buildSlices(aggregateBy(data?.origins ?? [], (l) => deriveCity(l) ?? 'Unbekannt'), 0),
    [data],
  );

  // Land: über die Geo-API (Nominatim). Jeder bekannte Geburtsort wird
  // geocodiert und über das letzte Segment des Treffers dem Land zugeordnet –
  // das funktioniert einheitlich für „Stadt", „Stadt, Land" und „nur Land".
  const [countryEntries, setCountryEntries] = useState<Array<{ label: string; percentage: number }> | null>(null);
  const [isGeocoding, setIsGeocoding] = useState(false);

  useEffect(() => {
    if (!data) {
      setCountryEntries(null);
      return;
    }

    const knownPlaces = data.origins.filter((o) => o.label !== 'Unbekannt').map((o) => o.label);
    if (knownPlaces.length === 0) {
      setIsGeocoding(false);
      setCountryEntries([]);
      return;
    }

    // Bei schnellem Personenwechsel darf ein laufendes Geocoding das Ergebnis
    // der neuen Person nicht überschreiben.
    let cancelled = false;
    setIsGeocoding(true);
    setCountryEntries(null);

    geocodePlaces(knownPlaces).then((geo) => {
      if (cancelled) return;
      const weights = new Map<string, number>();
      for (const o of data.origins) {
        const country =
          o.label === 'Unbekannt' ? 'Unbekannt' : countryFromGeo(geo.get(o.label)) ?? 'Unbekannt';
        weights.set(country, (weights.get(country) ?? 0) + o.weight);
      }
      const total = Array.from(weights.values()).reduce((a, b) => a + b, 0);
      const entries = Array.from(weights.entries())
        .map(([label, weight]) => ({
          label,
          weight,
          percentage: total > 0 ? Math.round((weight / total) * 1000) / 10 : 0,
        }))
        .sort((a, b) => b.weight - a.weight);
      setCountryEntries(entries);
      setIsGeocoding(false);
    });

    return () => {
      cancelled = true;
    };
  }, [data]);

  const countrySlices = useMemo(() => buildSlices(countryEntries ?? [], 3), [countryEntries]);

  if (isLoading || !data || data.totalAncestors === 0) return null;

  return (
    <div className="space-y-4">
      <h4 className="text-xs font-medium text-slate-400">
        Herkunft ({data.totalAncestors} Vorfahren analysiert)
      </h4>

      {/* Diagramm 1: Geburtsorte (Stadt/Region ohne Landsegment) */}
      <DonutChart slices={citySlices} title="Geburtsorte" />

      <hr className="border-slate-100" />

      {/* Diagramm 2: Länder (über die Geo-API/Nominatim) */}
      {isGeocoding ? (
        <div>
          <p className="mb-2 text-xs font-medium text-slate-400">Länder</p>
          <div className="flex items-center gap-2 py-2 text-xs text-slate-400">
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-slate-300 border-t-blue-500" />
            Orte werden zugeordnet…
          </div>
        </div>
      ) : countrySlices.length > 0 ? (
        <DonutChart slices={countrySlices} title="Länder" />
      ) : null}
    </div>
  );
}
